import React from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { toast } from "sonner";

const badgeColor = {
  New: { bg: "#E8A0B4", text: "#4A2C35" },
  Hot: { bg: "#D4537E", text: "#FDF6F0" },
  "Selling Fast": { bg: "#C5985A", text: "#FDF6F0" },
  "Only 3 Left": { bg: "#4A2C35", text: "#FDF6F0" },
  Bestseller: { bg: "#C5985A", text: "#FDF6F0" },
  Festive: { bg: "#D4537E", text: "#FDF6F0" },
  Set: { bg: "#4A2C35", text: "#FDF6F0" },
};

const ProductCard = ({ product, compact = false, search = "" }) => {
  const { addToCart, toggleWishlist, wishlist } = useStore();
  const inWishlist = wishlist.includes(product.id);
  const discount = product.mrp && product.mrp > product.price
    ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
    : 0;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
    toast.success(`${product.name} added to bag`);
  };

  const handleWish = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product.id);
    toast.success(inWishlist ? "Removed from wishlist" : "Added to wishlist");
  };

  const bColor = product.badge ? badgeColor[product.badge] || { bg: "#E8A0B4", text: "#4A2C35" } : null;

  return (
    <Link
      to={`/product/${product.id}${search}`}
      className={`product-card group block ${compact ? "w-[200px] sm:w-[240px]" : ""}`}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded-[18px] bg-pearl mb-4">
        <div className="img-wrap w-full h-full">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        {/* Badges */}
        {product.badge && (
          <span
            className="absolute top-3 left-3 text-[10px] tracking-[0.16em] uppercase font-medium px-3 py-1.5 rounded-full"
            style={{ background: bColor.bg, color: bColor.text }}
            data-testid={`badge-${product.id}`}
          >
            {product.badge}
          </span>
        )}

        {/* Wishlist heart */}
        <button
          onClick={handleWish}
          className={`absolute top-3 right-3 w-9 h-9 rounded-full bg-cream/90 backdrop-blur-sm flex items-center justify-center transition-all hover:bg-cream ${
            inWishlist ? "text-blush" : "text-plum/60 opacity-0 group-hover:opacity-100"
          } ${inWishlist ? "opacity-100" : ""}`}
          aria-label="Add to wishlist"
          data-testid={`wishlist-${product.id}`}
        >
          <Heart size={15} className={inWishlist ? "fill-blush" : ""} />
        </button>

        {/* Quick add (slides up on hover) */}
        <div className="quick-add absolute bottom-0 left-0 right-0 p-3">
          <button
            onClick={handleAddToCart}
            className="w-full bg-plum text-cream py-3 rounded-full text-[11px] tracking-[0.18em] uppercase font-medium flex items-center justify-center gap-2 hover:bg-blush transition-colors"
            data-testid={`add-to-cart-${product.id}`}
          >
            <ShoppingBag size={13} />
            Add to bag
          </button>
        </div>
      </div>

      <div className="px-1">
        <h3 className="font-serif text-plum text-[18px] lg:text-[19px] leading-tight mb-1 group-hover:italic transition-all">
          {product.name}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-plum font-medium text-[15px]">₹{product.price}</span>
          {product.mrp && product.mrp > product.price && (
            <>
              <span className="text-plum/40 text-[13px] line-through">₹{product.mrp}</span>
              <span className="text-[11px] text-blush font-medium tracking-wide">{discount}% off</span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
